import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import Markdown from 'react-markdown';
import {
  generateSummary,
  getPromptIds,
  getSummary,
  availableSummaryModels,
  Prompt,
  uploadFile,
  deleteUpload,
  UploadResponse,
} from '../../api/SummaryServiceV2';
import useLocalStorage from '../../hooks/useLocalStorage';
import { useSnackbar } from 'notistack';
import {
  convertMarkdownToDocx,
  convertMarkdownToTemplateDocx,
  downloadDocx,
} from '../../shared/markdownToDocx';
import { useDispatch, useSelector } from 'react-redux';
import { IStore } from '../../redux/store/IStore';
import { setSummarizingInProgress, setSummaryContent, setSummaryExists } from '../../redux/actions/actions';
import { EditorState, convertToRaw } from 'draft-js';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { config } from '../../config';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import Tooltip from '@material-ui/core/Tooltip';
import '../../styles/css/settingspopover.css';

interface SummaryModalContentProps {
  onClose: () => void;
  editorState: EditorState;
  sessionId: string;
}

// Simplified settings interface since we're not using the old SummaryService
interface SummarySettings {
  selectedModel: string;
  selectedPromptId: string;
}

// Map model values to display labels
const modelLabels = {
  'gpt-4o-mini': 'Standard',
  'gpt-5': 'Pro',
};

// This will be replaced with dynamic data from the API

const SummaryModalContent: React.FC<SummaryModalContentProps> = ({ onClose, editorState, sessionId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const summaryContentRef = useRef<HTMLDivElement>(null);

  const defaultSettings: SummarySettings = {
    selectedModel: config.summaryDefaultModel, // Default to Standard model
    selectedPromptId: '', // Will be set dynamically when prompts are loaded
  };

  const sessionName = useSelector((state: IStore) => state.sessionName);
  const summaryExists = useSelector((state: IStore) => state.summaryExists);
  const summarizingInProgress = useSelector((state: IStore) => state.summarizingInProgress);
  const summarizingAllowed = useSelector((state: IStore) => state.summarizingAllowed);
  const summaryContent = useSelector((state: IStore) => state.summaryContent);
  const userName = useSelector((state: IStore) => state.user?.username);

  const [summarySettings, setSummarySettings] = useState<SummarySettings>(defaultSettings);
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [availablePrompts, setAvailablePrompts] = useState<Prompt[]>([]);
  const [promptsLoading, setPromptsLoading] = useState<boolean>(true);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
  const [isAdditionalOpen, setIsAdditionalOpen] = useState<boolean>(false);
  const [placeholderFiles, setPlaceholderFiles] = useState<Record<string, File[]>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, UploadResponse[]>>({});
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());

  // Refs for optimized streaming updates
  const streamingContentRef = useRef<string>('');
  const rafIdRef = useRef<number | null>(null);
  const pendingUpdateRef = useRef<boolean>(false);
  const lastUpdateTimeRef = useRef<number>(0);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const UPDATE_THROTTLE_MS = 50; // Update at most every 50ms for smooth but efficient rendering

  const additionalPlaceholders = useMemo(() => {
    const selectedPrompt = availablePrompts.find((prompt) => prompt.id === summarySettings.selectedPromptId);
    return selectedPrompt?.placeholders.filter((placeholder) => placeholder.name !== 'transkript');
  }, [availablePrompts, summarySettings]);

  const totalFilesCount = useMemo(() => {
    const localFilesCount = Object.values(placeholderFiles).reduce((total, files) => total + files.length, 0);
    const uploadedFilesCount = Object.values(uploadedFiles).reduce((total, files) => total + files.length, 0);
    return localFilesCount + uploadedFilesCount;
  }, [placeholderFiles, uploadedFiles]);

  const { getSummarySettings } = useLocalStorage();

  // Optimized streaming content updater using requestAnimationFrame with throttling
  const updateStreamingContent = useCallback((newChunk: string) => {
    streamingContentRef.current += newChunk;

    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

    // Only schedule an update if one isn't already pending and enough time has passed
    if (!pendingUpdateRef.current && timeSinceLastUpdate >= UPDATE_THROTTLE_MS) {
      pendingUpdateRef.current = true;
      lastUpdateTimeRef.current = now;

      rafIdRef.current = requestAnimationFrame(() => {
        setStreamingContent(streamingContentRef.current);
        pendingUpdateRef.current = false;
        rafIdRef.current = null;
      });
    } else if (!pendingUpdateRef.current) {
      // Schedule a delayed update if throttle time hasn't passed yet
      pendingUpdateRef.current = true;
      const remainingTime = UPDATE_THROTTLE_MS - timeSinceLastUpdate;

      // Clear any existing timeout
      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
      }

      timeoutIdRef.current = setTimeout(() => {
        if (rafIdRef.current === null) {
          rafIdRef.current = requestAnimationFrame(() => {
            setStreamingContent(streamingContentRef.current);
            lastUpdateTimeRef.current = Date.now();
            pendingUpdateRef.current = false;
            rafIdRef.current = null;
            timeoutIdRef.current = null;
          });
        }
      }, remainingTime);
    }
  }, []);

  // Cleanup on unmount or when streaming stops
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      pendingUpdateRef.current = false;
    };
  }, []);

  // Auto-scroll to bottom when streaming content changes
  useEffect(() => {
    if (summarizingInProgress && summaryContentRef.current) {
      summaryContentRef.current.scrollTop = summaryContentRef.current.scrollHeight;
    }
  }, [streamingContent, summarizingInProgress]);

  // Fetch available prompts on component mount
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        setPromptsLoading(true);
        const prompts = await getPromptIds();
        setAvailablePrompts(prompts);
      } catch (error) {
        console.error('Failed to fetch prompts:', error);
        enqueueSnackbar('Napaka pri pridobivanju tipov povzetka.', { variant: 'error' });
      } finally {
        setPromptsLoading(false);
      }
    };

    fetchPrompts();
  }, [enqueueSnackbar]);

  // Reset uploaded files when session changes
  useEffect(() => {
    setUploadedFiles({});
    setPlaceholderFiles({});
    setUploadingFiles(new Set());
  }, [sessionId]);

  // Load settings from local storage
  useEffect(() => {
    const settings = getSummarySettings();

    setSummarySettings((prev) => ({
      ...prev,
      ...settings, // Merge stored settings with defaults
    }));
  }, [getSummarySettings]);

  // Fetch existing summary when modal opens
  useEffect(() => {
    const fetchExistingSummary = async () => {
      if (!sessionId) return;

      try {
        setSummaryLoading(true);
        const summaryResponse = await getSummary(sessionId);

        // Update Redux state with fetched summary
        dispatch(setSummaryContent(summaryResponse.content));
        dispatch(setSummaryExists(true));

        // Update settings with the summary's config if available
        if (summaryResponse.config) {
          setSummarySettings((prev) => ({
            ...prev,
            selectedModel: summaryResponse.config.model || prev.selectedModel,
            selectedPromptId: summaryResponse.config.promptId || prev.selectedPromptId,
          }));
        }
      } catch (error) {
        // If summary doesn't exist or there's an error, that's fine
        // Just reset the summary state
        dispatch(setSummaryContent(''));
        dispatch(setSummaryExists(false));

        console.log('No existing summary found or error fetching summary:', error);
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchExistingSummary();
  }, [sessionId, dispatch]);

  // Set default prompt when no summary exists and prompts are loaded
  useEffect(() => {
    if (availablePrompts.length > 0 && !summaryExists && !summarySettings.selectedPromptId) {
      const defaultPrompt = availablePrompts.find((prompt) => prompt.is_default) || availablePrompts[0];
      setSummarySettings((prev) => ({
        ...prev,
        selectedPromptId: defaultPrompt.id,
      }));
    }
  }, [availablePrompts, summaryExists, summarySettings.selectedPromptId]);

  // Save settings to local storage whenever they change
  useEffect(() => {
    if (userName) {
      localStorage.setItem(`editor-${userName}-summarize-v2-settings`, JSON.stringify(summarySettings));
    }
  }, [summarySettings, userName]);

  const handleSummarySettingChange = (key: keyof SummarySettings, value: any) => {
    setSummarySettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFileAdd = async (placeholderName: string, files: FileList | null) => {
    if (!files) return;

    const newFiles = Array.from(files);

    // Validate file types and sizes
    const validFiles: File[] = [];
    const maxFileSize = 20 * 1024 * 1024; // 20MB limit
    const allowedTypes = [
      'application/pdf', // PDF
      'text/plain', // TXT
      'application/msword', // DOC
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
      'application/vnd.oasis.opendocument.text', // ODT
    ];

    newFiles.forEach((file) => {
      if (file.size > maxFileSize) {
        enqueueSnackbar(`Datoteka "${file.name}" je preveč velika (maksimalno 20MB).`, {
          variant: 'warning',
        });
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        enqueueSnackbar(`Tip datoteke "${file.name}" ni podprt.`, { variant: 'warning' });
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      // Upload files to server
      const uploadPromises = validFiles.map(async (file) => {
        const fileKey = `${placeholderName}-${file.name}-${file.lastModified}`;
        setUploadingFiles((prev) => new Set(prev).add(fileKey));

        try {
          const uploadResponse = await uploadFile(file, placeholderName);
          return uploadResponse;
        } catch (error) {
          console.error('Failed to upload file:', error);
          enqueueSnackbar(`Napaka pri nalaganju datoteke "${file.name}".`, { variant: 'error' });
          return null;
        } finally {
          setUploadingFiles((prev) => {
            const newSet = new Set(prev);
            newSet.delete(fileKey);
            return newSet;
          });
        }
      });

      const uploadResults = await Promise.all(uploadPromises);
      const successfulUploads = uploadResults.filter((result): result is UploadResponse => result !== null);

      if (successfulUploads.length > 0) {
        setUploadedFiles((prev) => ({
          ...prev,
          [placeholderName]: [...(prev[placeholderName] || []), ...successfulUploads],
        }));

        if (successfulUploads.length < validFiles.length) {
          enqueueSnackbar(
            `${successfulUploads.length} od ${validFiles.length} datotek je bilo uspešno naloženih.`,
            {
              variant: 'info',
            }
          );
        } else {
          enqueueSnackbar(`${successfulUploads.length} datotek je bilo uspešno naloženih.`, {
            variant: 'success',
          });
        }
      }
    }
  };

  const handleFileRemove = async (
    placeholderName: string,
    fileIndex: number,
    isUploaded: boolean = false
  ) => {
    if (isUploaded) {
      const uploadedFile = uploadedFiles[placeholderName]?.[fileIndex];
      if (uploadedFile) {
        try {
          await deleteUpload(uploadedFile.id);
          setUploadedFiles((prev) => ({
            ...prev,
            [placeholderName]: prev[placeholderName]?.filter((_, index) => index !== fileIndex) || [],
          }));
          enqueueSnackbar(`Datoteka "${uploadedFile.original_name}" je bila odstranjena.`, {
            variant: 'success',
          });
        } catch (error) {
          console.error('Failed to delete uploaded file:', error);
          enqueueSnackbar(`Napaka pri odstranjevanju datoteke "${uploadedFile.original_name}".`, {
            variant: 'error',
          });
        }
      }
    } else {
      setPlaceholderFiles((prev) => ({
        ...prev,
        [placeholderName]: prev[placeholderName]?.filter((_, index) => index !== fileIndex) || [],
      }));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  function removeTrailingHashes(input: string) {
    return (
      input
        .split('\n') // Split into lines to process each line separately
        .map((line) =>
          line.replace(/^(#+)(.*?)\s*#+\s*$/, (_, hashesBefore, text) => {
            // Correctly return only the leading hashes and text
            return `${hashesBefore} ${text.trim()}`;
          })
        )
        .join('\n') // Join lines back together
        // Only normalize excessive newlines, don't force double newlines everywhere
        .replace(/\n{3,}/g, '\n\n')
        // Preserve single newlines within lists and paragraphs
        .trim()
    );
  }

  const stripBacktickFences = (input: string): string => {
    if (!input) {
      return '';
    }
    return input.replace(/```+/g, '').replace(/`+/g, '');
  };

  /**
   * Extracts plain text with speaker names from editor state.
   * Only includes speaker names (not all metadata) to keep it lightweight.
   * Format: "Speaker Name:\ntext content"
   * Only adds speaker name when it changes from the previous block.
   */
  const extractPlainTextWithSpeakers = (editorState: EditorState): string => {
    const rawContent = convertToRaw(editorState.getCurrentContent());
    const blocks = rawContent.blocks;

    if (!blocks || blocks.length === 0) {
      return '';
    }

    let prevSpeakerId: number | null = null;
    const lines: string[] = [];

    blocks.forEach((block) => {
      const speaker = block.data?.speaker;
      const text = block.text?.trim() || '';

      // Skip empty blocks
      if (!text) {
        return;
      }

      const currentSpeakerId = speaker?.id ?? null;

      // Add speaker name if it changed from previous block
      if (currentSpeakerId !== prevSpeakerId && speaker?.name) {
        lines.push(`${speaker.name}:`);
      }

      // Update previous speaker ID
      prevSpeakerId = currentSpeakerId;

      // Add the text content
      lines.push(text);
    });

    return lines.join('\n');
  };

  const handleExportTemplateDocx = (templateType: 'GE' | 'AJ') => {
    const sanitizedSummary = stripBacktickFences(summaryContent);
    const summaryContentToExport: string = removeTrailingHashes(sanitizedSummary);

    convertMarkdownToTemplateDocx(summaryContentToExport, templateType)
      .then((blob) => {
        const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const summaryOutputFilename = sessionName + ' (povzetek) - ' + currentDate + '.docx';
        downloadDocx(blob, summaryOutputFilename);
      })
      .catch((error) => {
        console.error('Error exporting to Template doc:', error);
        enqueueSnackbar('Napaka pri izvozu povzetka. Prosimo poizkusite znova.', { variant: 'error' });
      });
  };

  const handleExportToDoc = () => {
    const sanitizedSummary = stripBacktickFences(summaryContent);
    const summaryContentToExport: string = removeTrailingHashes(sanitizedSummary);
    convertMarkdownToDocx(summaryContentToExport)
      .then((blob) => {
        const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const summaryOutputFilename = sessionName + ' (povzetek) - ' + currentDate + '.docx';
        downloadDocx(blob, summaryOutputFilename);
      })
      .catch((error) => {
        enqueueSnackbar('Napaka pri izvozu povzetka. Prosimo poizkusite znova.', { variant: 'error' });
      });
  };

  const handleSummarizeButtonClick = async () => {
    // Console log file list and placeholders
    console.log('=== SUMMARY GENERATION DEBUG ===');
    console.log('Placeholders:', additionalPlaceholders);
    console.log('Uploaded files by placeholder:', uploadedFiles);

    // Log detailed file information
    Object.entries(uploadedFiles).forEach(([placeholderName, files]) => {
      console.log(
        `Placeholder "${placeholderName}":`,
        files.map((file) => ({
          id: file.id,
          filename: file.filename,
          size: file.size,
          placeholder: file.placeholder,
        }))
      );
    });
    console.log('================================');

    // Get the plain text content with speaker names from the editor state
    const sessionContent = extractPlainTextWithSpeakers(editorState);

    if (!sessionContent.trim()) {
      enqueueSnackbar('Ni besedila za povzeti.', { variant: 'error' });
      return;
    }

    // Check if prompts are still loading
    if (promptsLoading) {
      enqueueSnackbar('Nalagam tipove povzetka, prosimo počakajte.', { variant: 'warning' });
      return;
    }

    // Check if we have any prompts available
    if (availablePrompts.length === 0) {
      enqueueSnackbar('Ni na voljo tipov povzetka.', { variant: 'error' });
      return;
    }

    // Use the selected prompt ID directly
    const promptId = summarySettings.selectedPromptId;

    // Validate that the selected prompt ID is available
    const selectedPrompt = availablePrompts.find((p) => p.id === promptId);
    if (!selectedPrompt) {
      enqueueSnackbar('Izbrani tip povzetka ni na voljo.', { variant: 'error' });
      return;
    }

    // Prepare placeholder files mapping
    const placeholderFilesMapping: { [placeholderName: string]: string[] } = {};
    Object.entries(uploadedFiles).forEach(([placeholderName, files]) => {
      if (files.length > 0) {
        placeholderFilesMapping[placeholderName] = files.map((file) => file.id);
      }
    });

    // Reset streaming content and start new summary
    streamingContentRef.current = '';
    setStreamingContent('');
    dispatch(setSummarizingInProgress(true));

    generateSummary(
      summarySettings.selectedModel, // Pass the selected model as first parameter
      sessionContent,
      sessionId,
      promptId,
      (chunk: string) => {
        // Handle streaming chunks - use optimized batched updates
        updateStreamingContent(chunk);
      },
      (fullText: string) => {
        // Cancel any pending animation frame updates and timeouts
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        if (timeoutIdRef.current !== null) {
          clearTimeout(timeoutIdRef.current);
          timeoutIdRef.current = null;
        }
        pendingUpdateRef.current = false;

        // Handle completion - update Redux state and show final content
        dispatch(setSummaryContent(fullText));
        dispatch(setSummaryExists(true));
        dispatch(setSummarizingInProgress(false));
        streamingContentRef.current = '';
        setStreamingContent(''); // Clear streaming content
        enqueueSnackbar('Povzetek je pripravljen.', { variant: 'success' });
      },
      (error: any) => {
        // Cancel any pending animation frame updates and timeouts
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        if (timeoutIdRef.current !== null) {
          clearTimeout(timeoutIdRef.current);
          timeoutIdRef.current = null;
        }
        pendingUpdateRef.current = false;

        // Handle error
        console.error('Summary generation error:', error);
        dispatch(setSummarizingInProgress(false));
        streamingContentRef.current = '';
        setStreamingContent(''); // Clear streaming content on error
        enqueueSnackbar('Napaka pri generiranju povzetka. Prosimo poizkusite znova.', { variant: 'error' });
      },
      Object.keys(placeholderFilesMapping).length > 0 ? placeholderFilesMapping : undefined
    );
  };

  // Memoize display content to prevent unnecessary recalculations
  const displayContent = useMemo(() => {
    return summarizingInProgress ? streamingContent : summaryContent;
  }, [summarizingInProgress, streamingContent, summaryContent]);

  const sanitizedDisplayContent = useMemo(() => {
    if (!displayContent) {
      return '';
    }
    return stripBacktickFences(displayContent);
  }, [displayContent]);

  const hasContentToShow = useMemo(() => {
    return sanitizedDisplayContent.trim().length > 0;
  }, [sanitizedDisplayContent]);

  const isLoadingSummary = summaryLoading;

  // Memoize Markdown component props to prevent unnecessary re-renders
  const markdownComponents = useMemo(
    () => ({
      p: ({ children }: any) => <p>{children}</p>,
      br: () => <br />,
      pre: ({ children }: any) => <>{children}</>,
      code: ({ children }: any) => <>{children}</>,
    }),
    []
  );

  const enableExport = summaryExists && !summarizingInProgress && summaryContent.trim().length > 0;

  return (
    <div className="summary_modal_wrapper">
      <div className="summary_modal_content">
        <div className="summary_modal_title">Povzetek</div>
        <div className="summary_modal_settings">
          {/* Note: Summary settings are not currently used by the new generateSummary service
               but are kept for future enhancements and user preference storage */}

          <div className="summary_modal_section">
            <div className="summary_modal_dropdown">
              <TextField
                select
                value={summarySettings.selectedModel}
                onChange={(e) => handleSummarySettingChange('selectedModel', e.target.value)}
                style={{
                  fontSize: '14px',
                  minWidth: '250px',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" className="input_adornment">
                      Model
                    </InputAdornment>
                  ),
                }}
              >
                {availableSummaryModels.map((model) => (
                  <MenuItem key={model} value={model}>
                    {modelLabels[model as keyof typeof modelLabels] || model}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          </div>

          <div className="summary_modal_section">
            <div className="summary_modal_dropdown">
              <TextField
                select
                value={summarySettings.selectedPromptId}
                onChange={(e) => handleSummarySettingChange('selectedPromptId', e.target.value)}
                disabled={promptsLoading}
                style={{
                  fontSize: '14px',
                  minWidth: '500px',
                  opacity: promptsLoading ? 0.6 : 1,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" className="input_adornment">
                      {promptsLoading ? 'Nalagam...' : 'Tip povzetka'}
                    </InputAdornment>
                  ),
                }}
              >
                {promptsLoading ? (
                  <MenuItem disabled value="">
                    Nalagam tipove povzetka...
                  </MenuItem>
                ) : availablePrompts.length === 0 ? (
                  <MenuItem disabled value="">
                    Ni tipov povzetka na voljo
                  </MenuItem>
                ) : (
                  availablePrompts.map((prompt) => (
                    <MenuItem key={prompt.id} value={prompt.id}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {prompt.namen}
                        {prompt.is_private && (
                          <span title="Private" style={{ fontSize: '12px' }}>
                            🔒
                          </span>
                        )}
                      </span>
                    </MenuItem>
                  ))
                )}
              </TextField>
            </div>
          </div>
        </div>
        {additionalPlaceholders && additionalPlaceholders.length > 0 && (
          <div className="summary_modal_additional_files_wrapper">
            <button
              type="button"
              className="summary_modal_additional_toggle"
              onClick={() => setIsAdditionalOpen((prev) => !prev)}
            >
              <span>
                Dodatne datoteke
                {totalFilesCount > 0 && (
                  <span className="summary_modal_total_file_count"> ({totalFilesCount})</span>
                )}
              </span>
              <span className={`summary_modal_additional_toggle_icon ${isAdditionalOpen ? 'open' : ''}`}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.12 9.29L12 13.17L15.88 9.29C16.27 8.9 16.9 8.9 17.29 9.29C17.68 9.68 17.68 10.31 17.29 10.7L12.7 15.29C12.31 15.68 11.68 15.68 11.29 15.29L6.7 10.7C6.31 10.31 6.31 9.68 6.7 9.29C7.09 8.91 7.73 8.9 8.12 9.29Z"
                    fill="#333"
                  />
                </svg>
              </span>
            </button>
            {isAdditionalOpen && (
              <div className="summary_modal_additional_body">
                <div className="summary_modal_additional_files">
                  {additionalPlaceholders.map((placeholder) => (
                    <div key={placeholder.name} className="summary_modal_placeholder_section">
                      <Tooltip title={placeholder.description || ''} arrow placement="top">
                        <div className="summary_modal_additional_file_label">
                          {placeholder.label}
                          {uploadedFiles[placeholder.name] && uploadedFiles[placeholder.name].length > 0 && (
                            <span className="summary_modal_file_count">
                              ({uploadedFiles[placeholder.name].length})
                            </span>
                          )}
                        </div>
                      </Tooltip>

                      {/* File Upload Area */}
                      <div className="summary_modal_file_upload_area">
                        <input
                          type="file"
                          multiple
                          accept=".txt,.pdf,.docx,.odt,.doc"
                          onChange={(e) => handleFileAdd(placeholder.name, e.target.files)}
                          className="summary_modal_file_input"
                          id={`file-input-${placeholder.name}`}
                        />
                        <Tooltip
                          title="Dovoljeni tipi datotek: TXT, PDF, DOCX, ODT, DOC (maksimalno 20MB)"
                          arrow
                          placement="top"
                        >
                          <label
                            htmlFor={`file-input-${placeholder.name}`}
                            className={`button_primary summary_modal_button ${
                              uploadingFiles.size > 0 ? 'uploading' : ''
                            }`}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M14 2V8H20"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M16 13H8"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M16 17H8"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M10 9H9H8"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            {uploadingFiles.size > 0 ? 'Nalagam...' : 'Dodaj datoteke'}
                          </label>
                        </Tooltip>
                      </div>

                      {/* File List */}
                      {uploadedFiles[placeholder.name] && uploadedFiles[placeholder.name].length > 0 && (
                        <div className="summary_modal_file_list">
                          {uploadedFiles[placeholder.name].map((file, index) => (
                            <div key={file.id} className="summary_modal_file_item">
                              <div className="summary_modal_file_info">
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M14 2V8H20"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <div className="summary_modal_file_details">
                                  <span className="summary_modal_file_name">
                                    {file.original_name || 'Unknown file'}
                                  </span>
                                  <span className="summary_modal_file_size">
                                    {file.size_bytes && typeof file.size_bytes === 'number'
                                      ? formatFileSize(file.size_bytes)
                                      : 'Unknown size'}
                                  </span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="summary_modal_file_remove"
                                onClick={() => handleFileRemove(placeholder.name, index, true)}
                                title="Odstrani datoteko"
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M18 6L6 18"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M6 6L18 18"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <div>
          <div className="summary_modal_section summary_modal_buttons">
            {/* This is a special .docx template done for GEN energija  */}
            {/* Its a super temporary solution - we should develop this on the Backend */}
            {/* This is of course if we get time to do this :)  */}
            {enableExport && (
              <button
                className="button_primary summary_modal_button"
                onClick={() => {
                  if (!userName) {
                    handleExportToDoc();
                    return;
                  }

                  const isGenEnergijaUser = userName.endsWith('gen-energija.si');
                  const isAjdovscinaUser = userName.endsWith('ajdovscina.si');

                  if (isGenEnergijaUser) {
                    handleExportTemplateDocx('GE');
                  } else if (isAjdovscinaUser) {
                    handleExportTemplateDocx('AJ');
                  } else {
                    handleExportToDoc();
                  }
                }}
              >
                Izvozi
              </button>
            )}
            <button
              className={`button_primary summary_modal_button ${
                summarizingInProgress || !summarizingAllowed ? 'summary_button_disabled' : ''
              }`}
              disabled={summarizingInProgress || !summarizingAllowed}
              onClick={handleSummarizeButtonClick}
            >
              {summarizingInProgress ? (
                <>
                  <span className="summary_spinner" />
                  Povzemam ...
                </>
              ) : (
                'Povzemi'
              )}
            </button>
          </div>
        </div>
        <div className="summary_content" ref={summaryContentRef}>
          {isLoadingSummary ? (
            <div className="summary_loading_indicator">
              <span className="summary_spinner" />
              Nalagam povzetek...
            </div>
          ) : hasContentToShow ? (
            <>
              <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
                {sanitizedDisplayContent}
              </Markdown>
              {summarizingInProgress && (
                <div className="streaming_indicator">
                  <span className="streaming_cursor">|</span>
                  <span className="streaming_progress">({displayContent.length} znakov)</span>
                </div>
              )}
            </>
          ) : (
            <div className="summary_empty_state">
              Ni povzetka na voljo. Kliknite "Povzemi" za generiranje novega povzetka.
            </div>
          )}
        </div>
        <div className="summary_modal_section summary_modal_close_button">
          <button type="button" className="modal_button_disaggre" onClick={onClose}>
            Zapri
          </button>
        </div>
      </div>
    </div>
  );
};

export default SummaryModalContent;
